/**
 * Human-readable display for master formulas that reference subformulas via
 * `__sub_<id>` tokens. Expands each ref to the subformula expression (recursive,
 * cycle-safe) so the UI shows the full calculation, not internal ids.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FormulaSubformula } from "../voluum-flow-optimizer.types";
import { SUBFORMULA_REF_PREFIX } from "../voluum-flow-optimizer.types";
import { normalizeSubformulaWeights } from "./subformula-weights";

const DEFAULT_MAX_DEPTH = 12;

/** Replace every `__sub_<id>` with `(<sub expression>)` or sub name / 0. */
export function expandSubrefsForDisplay(
  expression: string,
  subformulas: readonly FormulaSubformula[],
  options?: { maxDepth?: number },
): string {
  const trimmed = expression.trim();
  if (!trimmed || subformulas.length === 0) return trimmed;
  const byId = buildSubformulaLookup(subformulas);
  const maxDepth = options?.maxDepth ?? DEFAULT_MAX_DEPTH;
  return expandDepth(trimmed, byId, maxDepth, 0, new Set());
}

function buildSubformulaLookup(
  subformulas: readonly FormulaSubformula[],
): Map<string, FormulaSubformula> {
  const byId = new Map<string, FormulaSubformula>();
  for (const s of subformulas) {
    if (s && typeof s.id === "string") byId.set(s.id, s);
  }
  return byId;
}

function expandDepth(
  text: string,
  byId: Map<string, FormulaSubformula>,
  maxDepth: number,
  depth: number,
  expanding: Set<string>,
): string {
  if (depth > maxDepth) return text;
  const pattern = subrefPattern();
  return text.replace(pattern, (full, subId: string) => {
    if (expanding.has(subId)) return full;
    const sub = byId.get(subId);
    if (!sub) {
      return `(unknown subformula: ${subId})`;
    }
    const inner = (sub.expression ?? "").trim();
    if (!inner) return sub.name?.trim() || "0";
    expanding.add(subId);
    const expanded = expandDepth(inner, byId, maxDepth, depth + 1, expanding);
    expanding.delete(subId);
    return `(${expanded})`;
  });
}

/** Fresh RegExp each use — avoids `/g` `lastIndex` bugs across calls. */
function subrefPattern(): RegExp {
  return new RegExp(`${escapeRegExp(SUBFORMULA_REF_PREFIX)}([a-zA-Z0-9_]+)`, "g");
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * The calculation a blocks group REALLY runs, readable: each `__sub_<id>`
 * becomes `(<its expression>)`, times its normalized weight when the
 * sub-formulas are weighted (exactly what the scorer substitutes) —
 * "(cr + cv) * 0.70 + (roi) * 0.30".
 */
export function expandSubrefsWithWeights(
  expression: string,
  subformulas: readonly FormulaSubformula[],
): string {
  const weighted = subformulas.some((s) => typeof s.weight === "number")
    ? normalizeSubformulaWeights(subformulas)
    : null;
  if (!weighted) return expandSubrefsForDisplay(expression, subformulas);
  const byId = buildSubformulaLookup(subformulas);
  return expression.trim().replace(subrefPattern(), (_full, id: string) => {
    const sub = byId.get(id);
    if (!sub) return `(unknown subformula: ${id})`;
    const inner = expandSubrefsForDisplay((sub.expression ?? "").trim() || "0", subformulas);
    const w = weighted[id] ?? 0;
    return w === 100 ? `(${inner})` : `(${inner}) * ${(w / 100).toFixed(2)}`;
  });
}
